import React from "react";
import ReactDOM from "react-dom/client";
import "./assets/styles/bootstrap.custom.css";
// import "bootstrap/dist/css/bootstrap.min.css";

import "./assets/styles/index.css";
import App from "./App";
import reportWebVitals from "./reportWebVitals";

import {
  createBrowserRouter,
  createRoutesFromElements,
  Route,
  RouterProvider,
} from "react-router-dom";

import { HelmetProvider } from "react-helmet-async";
import { Provider, useSelector } from "react-redux";
import store from "./store";

// Screens
import LoginScreen from "./screens/LoginScreen";
import RegisterScreen from "./screens/RegisterScreen";
import ForgotPasswordScreen from "./screens/ForgotPasswordScreen";

import HomeScreen from "./screens/HomeScreen";
import SalesOrderScreen from "./screens/SalesOrderScreen";
import SearchSalesOrderScreen from "./screens/SearchSalesOrderScreen";
import VendorPendingOrdersScreen from "./screens/VendorPendingOrdersScreen";
import VendorOrderScreen from "./screens/VendorOrderScreen";
import CreateProductScreen from "./screens/CreateProductScreen";
import EditProductScreen from "./screens/EditProductScreen";
import VendorProductListScreen from "./screens/VendorProductListScreen";
import ProductDetailsScreen from "./screens/ProductDetailsScreen";
import ProfileScreen from "./screens/ProfileScreen";
import UpdateProfileScreen from "./screens/UpdateProfileScreen";
import VendorOrdersTable from "./screens/VendorOrdersTable";
import VendorExpiryProductsScreen from "./screens/VendorExpiryProductsScreen";
import ShippingScreen from "./screens/ShippingScreen";
import VendorToggleCard from "./screens/VendorToggleCard";
import CreateCategoryScreen from "./screens/CreateCategoryScreen";
import CategoryListScreen from "./screens/CategoryListScreen";
import UserCategoryListScreen from "./screens/UserCategoryListScreen";
import UserProductDetailsScreen from "./screens/UserProductDetailsScreen";
import UserProductSearch from "./screens/UserProductSearch";
import SubcategoryScreen from "./screens/SubcategoryScreen";
import EditCategoryScreen from "./screens/EditCategoryScreen";
import Unauthorized from "./screens/Unauthorized";
import CartScreen from "./screens/CartScreen";
import PlaceOrderScreen from "./screens/PlaceOrderScreen";
import OrderScreen from "./screens/OrderScreen";
import OrdersSummary from "./screens/OrdersSummary";
import CreateInformationScreen from "./screens/CreateInformationScreen";
import InformationListScreen from "./screens/InformationListScreen";
import UserListScreen from "./screens/UserListScreen";
import UserEditScreen from "./screens/UserEditScreen";
import ProductReferenceScreen from "./screens/ProductReferenceScreen";
import ProductCategory from "./screens/ProductCategory";
import InactiveVendorProductListScreen from "./screens/InactiveVendorProductListScreen";

// (Optional) Add these if you have separate components for user actions:
import MyOrdersScreen from "./screens/MyOrdersScreen";
import ProductSearchScreen from "./screens/ProductSearchScreen";
import ProductListScreen from "./screens/ProductListScreen";
import GenerateGSTR1Report from "./screens/GenerateGSTR1Report";
import PendingOrdersByArea from "./screens/PendingOrdersByArea";

// Role-based route guards
import ProtectedRoute from "./components/ProtectedRoute";
import AdminRoute from "./components/AdminRoute";

// Route wrapper component to access Redux state
const RouterWrapper = () => {
  const { userInfo } = useSelector((state) => state.auth);

  const router = createBrowserRouter(
    createRoutesFromElements(
      <Route path="/" element={<App />}>
        {/* Public Routes */}
        <Route index path="/" element={<LoginScreen />} />
        <Route path="/register" element={<RegisterScreen />} />
        <Route path="/forgot-password" element={<ForgotPasswordScreen />} />

        <Route path="/unauthorized" element={<Unauthorized />} />

        {/* Shared Home Route (All Roles) */}
        <Route
          path="/Home"
          element={
            <ProtectedRoute
              user={userInfo}
              allowedRoles={["user", "vendor", "admin", "delivery"]}
            >
              <HomeScreen />
            </ProtectedRoute>
          }
        />
        <Route
          path="/product-reference/:category"
          element={
            <ProtectedRoute
              user={userInfo}
              allowedRoles={["user", "vendor", "admin", "delivery"]}
            >
              <ProductReferenceScreen />
            </ProtectedRoute>
          }
        />
        <Route
          path="/productCategory"
          element={
            <ProtectedRoute
              user={userInfo}
              allowedRoles={["user", "vendor", "admin", "delivery"]}
            >
              <ProductCategory />
            </ProtectedRoute>
          }
        />
        {/* --- USER ROUTES --- */}
        <Route
          path="/my-orders"
          element={
            <ProtectedRoute user={userInfo} allowedRoles={["user", "vendor"]}>
              <MyOrdersScreen />
            </ProtectedRoute>
          }
        />
        <Route
          path="/user-categories"
          element={
            <ProtectedRoute user={userInfo} allowedRoles={["user"]}>
              <UserCategoryListScreen />
            </ProtectedRoute>
          }
        />
        <Route
          path="/subcategory/:categoryName"
          element={
            <ProtectedRoute user={userInfo} allowedRoles={["user"]}>
              <SubcategoryScreen />
            </ProtectedRoute>
          }
        />
        <Route
          path="/productlist/:subcategory"
          element={
            <ProtectedRoute user={userInfo} allowedRoles={["user"]}>
              <ProductListScreen />
            </ProtectedRoute>
          }
        />
        <Route
          path="/product/:productId"
          element={
            <ProtectedRoute user={userInfo} allowedRoles={["user", "vendor"]}>
              <UserProductDetailsScreen />
            </ProtectedRoute>
          }
        />
        <Route
          path="/cart"
          element={
            <ProtectedRoute user={userInfo} allowedRoles={["user"]}>
              <CartScreen />
            </ProtectedRoute>
          }
        />
        <Route
          path="/shipping"
          element={
            <ProtectedRoute user={userInfo} allowedRoles={["user"]}>
              <ShippingScreen />
            </ProtectedRoute>
          }
        />
        <Route
          path="/placeorder"
          element={
            <ProtectedRoute user={userInfo} allowedRoles={["user"]}>
              <PlaceOrderScreen />
            </ProtectedRoute>
          }
        />
        <Route
          path="/user-product-search"
          element={
            <ProtectedRoute user={userInfo} allowedRoles={["user"]}>
              <UserProductSearch />
            </ProtectedRoute>
          }
        />
        <Route
          path="/userOrder/:orderId"
          element={
            <ProtectedRoute user={userInfo} allowedRoles={["user", "vendor"]}>
              <OrderScreen />
            </ProtectedRoute>
          }
        />
        <Route
          path="/orders-summary"
          element={
            <ProtectedRoute user={userInfo} allowedRoles={["user"]}>
              <OrdersSummary />
            </ProtectedRoute>
          }
        />
        <Route
          path="/product-search"
          element={
            <ProtectedRoute user={userInfo} allowedRoles={["user"]}>
              <ProductSearchScreen />
            </ProtectedRoute>
          }
        />
        {/* If category list should be visible to users */}
        <Route
          path="/category"
          element={
            <ProtectedRoute user={userInfo} allowedRoles={["user", "admin"]}>
              <CategoryListScreen />
            </ProtectedRoute>
          }
        />

        {/* --- VENDOR ROUTES --- */}
        <Route
          path="/order"
          element={
            <ProtectedRoute user={userInfo} allowedRoles={["vendor", "admin"]}>
              <SalesOrderScreen />
            </ProtectedRoute>
          }
        />
        <Route
          path="/searchorder"
          element={
            <ProtectedRoute user={userInfo} allowedRoles={["vendor", "admin"]}>
              <SearchSalesOrderScreen />
            </ProtectedRoute>
          }
        />
        <Route
          path="/PendingOrders"
          element={
            <ProtectedRoute user={userInfo} allowedRoles={["vendor", "admin"]}>
              <VendorPendingOrdersScreen />
            </ProtectedRoute>
          }
        />
        <Route
          path="/vendor/orders/:orderId"
          element={
            <ProtectedRoute
              user={userInfo}
              allowedRoles={["vendor", "delivery", "admin"]}
            >
              <VendorOrderScreen />
            </ProtectedRoute>
          }
        />
        <Route
          path="/productList"
          element={
            <ProtectedRoute user={userInfo} allowedRoles={["vendor", "admin"]}>
              <VendorProductListScreen />
            </ProtectedRoute>
          }
        />
        <Route
          path="/expiryproductList"
          element={
            <ProtectedRoute user={userInfo} allowedRoles={["vendor", "admin"]}>
              <VendorExpiryProductsScreen />
            </ProtectedRoute>
          }
        />
        <Route
          path="/inActiveProducts"
          element={
            <ProtectedRoute user={userInfo} allowedRoles={["vendor", "admin"]}>
              <InactiveVendorProductListScreen />
            </ProtectedRoute>
          }
        />
        <Route
          path="/createProduct"
          element={
            <ProtectedRoute user={userInfo} allowedRoles={["vendor", "admin"]}>
              <CreateProductScreen />
            </ProtectedRoute>
          }
        />
        <Route
          path="/GSTReport"
          element={
            <ProtectedRoute user={userInfo} allowedRoles={["vendor", "admin"]}>
              <GenerateGSTR1Report />
            </ProtectedRoute>
          }
        />
        <Route
          path="/edit-product/:id"
          element={
            <ProtectedRoute user={userInfo} allowedRoles={["vendor", "admin"]}>
              <EditProductScreen />
            </ProtectedRoute>
          }
        />
        <Route
          path="/vendorOrdersList"
          element={
            <ProtectedRoute user={userInfo} allowedRoles={["vendor", "admin"]}>
              <VendorOrdersTable />
            </ProtectedRoute>
          }
        />

        {/* --- ADMIN ROUTES --- */}
        <Route
          path="/toggleCard"
          element={
            <AdminRoute user={userInfo}>
              <VendorToggleCard />
            </AdminRoute>
          }
        />
        <Route
          path="/create-category"
          element={
            <AdminRoute user={userInfo}>
              <CreateCategoryScreen />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/categories"
          element={
            <AdminRoute user={userInfo}>
              <CategoryListScreen />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/category/:id/edit"
          element={
            <AdminRoute user={userInfo}>
              <EditCategoryScreen />
            </AdminRoute>
          }
        />
        <Route
          path="/userList"
          element={
            <AdminRoute user={userInfo}>
              <UserListScreen />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/user/:id/edit"
          element={
            <AdminRoute user={userInfo}>
              <UserEditScreen />
            </AdminRoute>
          }
        />
        <Route
          path="/informationList"
          element={
            <AdminRoute user={userInfo}>
              <InformationListScreen />
            </AdminRoute>
          }
        />
        <Route
          path="/information"
          element={
            <AdminRoute user={userInfo}>
              <CreateInformationScreen />
            </AdminRoute>
          }
        />
        {/* --- DELIVERY ROUTES --- */}
        <Route
          path="/orders-by-area/:area"
          element={
            <ProtectedRoute
              user={userInfo}
              allowedRoles={["delivery", "admin"]}
            >
              <PendingOrdersByArea />
            </ProtectedRoute>
          }
        />
        {/* Shared route: Profile (optional) */}
        <Route
          path="/profile"
          element={
            <ProtectedRoute
              user={userInfo}
              allowedRoles={["user", "vendor", "admin", "delivery"]}
            >
              <ProfileScreen />
            </ProtectedRoute>
          }
        />
        <Route
          path="/update-profile"
          element={
            <ProtectedRoute
              user={userInfo}
              allowedRoles={["user", "vendor", "admin", "delivery"]}
            >
              <UpdateProfileScreen />
            </ProtectedRoute>
          }
        />
      </Route>
    )
  );

  return <RouterProvider router={router} />;
};

// Mount React app
const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <HelmetProvider>
      <Provider store={store}>
        <RouterWrapper />
      </Provider>
    </HelmetProvider>
  </React.StrictMode>
);

reportWebVitals();
